import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Sun,
  CloudRain,
  Snowflake,
  LocateFixed,
  Footprints,
  Ship,
  Route,
  PlaneTakeoff,
  Orbit,
  Volume2,
  VolumeX,
  Play,
  Pause,
  BookOpen,
  Glasses,
  MapPin,
  Bookmark,
  Car,
  Building2,
  Quote,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
} from "lucide-react";
import { useStore, getEffectiveWeather } from "../store";
import { LANDMARKS } from "../three/layout";

const gold = "#D4AF37";

function IconBtn({ active, onClick, title, children, testid, size = "md" }) {
  const pad = size === "sm" ? "p-2" : "p-2.5";
  return (
    <button
      data-testid={testid}
      title={title}
      onClick={onClick}
      className={`${pad} rounded-xl border transition-[transform,background-color,border-color] duration-200 hover:scale-[1.06] ${
        active
          ? "bg-[#D4AF37] border-[#D4AF37] text-[#0b0c10] shadow-[0_0_18px_rgba(212,175,55,0.5)]"
          : "bg-white/8 border-white/15 text-stone-200 hover:border-[#D4AF37]/60 hover:text-[#F4E3A1]"
      }`}
    >
      {children}
    </button>
  );
}

function DPad() {
  const setDpad = useStore((s) => s.setDpad);
  const muted = useStore((s) => s.muted);
  const toggleMuted = useStore((s) => s.toggleMuted);
  const set = (x, y) => setDpad({ x, y });
  const clear = () => setDpad({ x: 0, y: 0 });
  const hold = (x, y) => ({
    onPointerDown: (e) => { e.preventDefault(); set(x, y); },
    onPointerUp: clear,
    onPointerLeave: clear,
    onPointerCancel: clear,
  });
  const cls =
    "h-11 w-11 grid place-items-center rounded-xl bg-white/10 border border-white/20 text-stone-100 active:bg-[#D4AF37] active:text-[#0b0c10] transition-colors touch-none select-none";
  return (
    <div className="pointer-events-auto flex items-center gap-3 glass-dark rounded-2xl p-2">
      <div className="grid grid-cols-3 grid-rows-3 gap-1" style={{ width: 150 }}>
        <span />
        <button data-testid="dpad-up-btn" {...hold(0, 1)} className={cls}><ChevronUp size={18} /></button>
        <span />
        <button data-testid="dpad-left-btn" {...hold(-1, 0)} className={cls}><ChevronLeft size={18} /></button>
        <span />
        <button data-testid="dpad-right-btn" {...hold(1, 0)} className={cls}><ChevronRight size={18} /></button>
        <span />
        <button data-testid="dpad-down-btn" {...hold(0, -1)} className={cls}><ChevronDown size={18} /></button>
        <span />
      </div>
      <button
        data-testid="dpad-mute-btn"
        onClick={toggleMuted}
        className={`h-11 w-11 grid place-items-center rounded-full transition-colors ${muted ? "bg-white/10 text-stone-200" : "bg-[#D4AF37] text-[#0b0c10]"}`}
      >
        {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
      </button>
    </div>
  );
}

const NAV = [
  { key: "orbit", label: "Aerial Orbit", Icon: Orbit, testid: "nav-mode-orbit-btn" },
  { key: "walk", label: "Walk", Icon: Footprints, testid: "nav-mode-walk-btn" },
  { key: "drive", label: "Car", Icon: Car, testid: "nav-mode-drive-btn" },
  { key: "bridge", label: "Bridge Tour", Icon: Route, testid: "nav-mode-bridge-tour-btn" },
  { key: "hudson", label: "Hudson Cruise", Icon: Ship, testid: "nav-mode-hudson-cruise-btn" },
  { key: "helicopter", label: "Helicopter", Icon: PlaneTakeoff, testid: "nav-mode-helicopter-btn" },
  { key: "interior", label: "Interior Decks", Icon: Building2, testid: "nav-mode-interior-btn" },
];

const WEATHER = [
  { key: "sunny", Icon: Sun, testid: "weather-control-sunny-btn", title: "Sunny" },
  { key: "rain", Icon: CloudRain, testid: "weather-control-rain-btn", title: "Rain" },
  { key: "snow", Icon: Snowflake, testid: "weather-control-snow-btn", title: "Snow" },
  { key: "auto", Icon: LocateFixed, testid: "weather-control-auto-btn", title: "Auto (live)" },
];

export default function HUD({ onEnterVR, vrSupported }) {
  const temporal = useStore((s) => s.temporal);
  const setTemporal = useStore((s) => s.setTemporal);
  const timelapse = useStore((s) => s.timelapse);
  const toggleTimelapse = useStore((s) => s.toggleTimelapse);
  const timeOfDay = useStore((s) => s.timeOfDay);
  const setTimeOfDay = useStore((s) => s.setTimeOfDay);
  const weather = useStore((s) => s.weather);
  const effWeather = useStore((s) => getEffectiveWeather(s));
  const setWeather = useStore((s) => s.setWeather);
  const navMode = useStore((s) => s.navMode);
  const setNavMode = useStore((s) => s.setNavMode);
  const muted = useStore((s) => s.muted);
  const toggleMuted = useStore((s) => s.toggleMuted);
  const fps = useStore((s) => s.fps);
  const setGuestbookOpen = useStore((s) => s.setGuestbookOpen);
  const requestFocus = useStore((s) => s.requestFocus);
  const setActiveLandmark = useStore((s) => s.setActiveLandmark);
  const interiorFloor = useStore((s) => s.interiorFloor);
  const setInteriorFloor = useStore((s) => s.setInteriorFloor);
  const resetCamera = useStore((s) => s.resetCamera);

  const year = Math.floor(temporal);
  const era = temporal < 2013.5 ? "2001" : "2026";
  const hh = Math.floor(timeOfDay);
  const mm = Math.floor((timeOfDay - hh) * 60);
  const phase =
    temporal < 2008
      ? "Original Complex · The Sphere"
      : temporal < 2015
      ? "Transition · 1 WTC Rising"
      : "25th Anniversary Memorial";

  const focusLandmark = (l) => {
    setNavMode("orbit");
    setTemporal(l.era === "2001" ? 2001 : 2026);
    setActiveLandmark(l.key);
    requestFocus({ position: l.position, target: l.target });
  };

  const narrateTribute = () => {
    try {
      const synth = window.speechSynthesis;
      if (!synth) return;
      if (synth.speaking) {
        synth.cancel();
        return;
      }
      const u = new SpeechSynthesisUtterance(
        "We remember the nearly three thousand lives lost on September 11th, 2001. Their names are engraved in bronze around these sacred pools. We will never forget."
      );
      u.rate = 0.9;
      u.pitch = 1;
      synth.speak(u);
    } catch (e) {}
  };

  return (
    <div
      className="pointer-events-none fixed inset-0 z-30 select-none text-stone-100"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {/* ===== TAB seal — pinned top-left anchor ===== */}
      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="pointer-events-auto absolute top-4 left-4 flex items-center gap-3"
      >
        <div className="h-11 w-11 sm:h-[60px] sm:w-[60px] rounded-full overflow-hidden border border-[#D4AF37]/70 shadow-[0_4px_14px_rgba(0,0,0,0.55)] ring-1 ring-[#F4E3A1]/30 shrink-0">
          <img src="/tab-seal.png" alt="TAB seal" className="h-full w-full object-cover" data-testid="tab-logo-seal" />
        </div>
        <div className="leading-tight hidden sm:block">
          <h1 className="font-serif-display text-white text-xl sm:text-2xl font-light tracking-wide whitespace-nowrap">
            World Trade Center
          </h1>
          <p className="font-mono-tele text-[9px] sm:text-[10px] tracking-[0.25em] text-[#F4E3A1]/80 uppercase">
            Spatial Memorial · 25th Anniversary
          </p>
        </div>
      </motion.div>

      {/* ===== RIGHT: control cluster + Navigation menu ===== */}
      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className="pointer-events-auto absolute top-4 right-4 flex flex-col items-end gap-2 max-w-[86vw]"
      >
        {/* Remembrance ribbon */}
        <div className="flex flex-col items-center mb-0.5">
          <img src="/ribbon_t.png" alt="Remembrance ribbon" data-testid="never-forget-ribbon" className="h-10 sm:h-14 drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)]" />
          <p className="font-serif-display text-white text-[10px] sm:text-sm leading-tight text-center tracking-wide drop-shadow-[0_1px_4px_rgba(0,0,0,0.8)]">
            We Will Never Forget
          </p>
        </div>

        {/* environment controls */}
        <div className="flex flex-wrap items-center justify-end gap-1.5 glass-dark rounded-2xl px-2 py-2">
          {WEATHER.map(({ key, Icon, testid, title }) => (
            <IconBtn key={key} testid={testid} size="sm" title={title} active={weather === key} onClick={() => setWeather(key)}>
              <Icon size={15} />
            </IconBtn>
          ))}
          <div className="mx-0.5 h-6 w-px bg-white/15" />
          <IconBtn testid="audio-mute-toggle-btn" size="sm" title={muted ? "Unmute" : "Mute"} active={!muted} onClick={toggleMuted}>
            {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
          </IconBtn>
          <IconBtn testid="guestbook-modal-trigger" size="sm" title="Memorial Notes" onClick={() => setGuestbookOpen(true)}>
            <BookOpen size={15} />
          </IconBtn>
          <IconBtn testid="tribute-narrate-btn" size="sm" title="Narrated tribute" onClick={narrateTribute}>
            <Quote size={15} />
          </IconBtn>
          <IconBtn testid="reset-camera-btn" size="sm" title="Reset camera & zoom" onClick={resetCamera}>
            <RotateCcw size={15} />
          </IconBtn>
          {vrSupported && (
            <button
              data-testid="webxr-enter-vr-btn"
              onClick={onEnterVR}
              title="Enter VR"
              className="flex items-center gap-1.5 rounded-xl bg-[#D4AF37] text-[#0b0c10] px-2.5 py-2 text-xs font-semibold hover:scale-[1.05] transition-transform"
            >
              <Glasses size={15} />
            </button>
          )}
        </div>

        {/* Navigation / Transit menu */}
        <div className="glass-dark rounded-2xl p-2">
          <div className="font-mono-tele text-[8px] tracking-[0.25em] text-[#F4E3A1]/70 uppercase px-1 pb-1.5 text-right">
            Navigation
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-7 gap-1.5">
            {NAV.map(({ key, label, Icon, testid }) => (
              <IconBtn key={key} testid={testid} title={label} active={navMode === key} onClick={() => setNavMode(key)} size="sm">
                <Icon size={16} />
              </IconBtn>
            ))}
          </div>
          {navMode === "interior" && (
            <div className="mt-1.5 flex gap-1.5 justify-end">
              {[
                ["f107", "North · 107"],
                ["f81", "South · 81"],
                ["roof", "South · Roof"],
              ].map(([f, lbl]) => (
                <button
                  key={f}
                  data-testid={`interior-floor-${f}-btn`}
                  onClick={() => setInteriorFloor(f)}
                  className={`rounded-lg px-2.5 py-1 text-[10px] font-mono-tele tracking-widest transition-colors ${
                    interiorFloor === f ? "bg-[#D4AF37] text-[#0b0c10]" : "bg-white/8 text-stone-200 hover:bg-white/15"
                  }`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Landmark quick-focus */}
        <div className="hidden lg:flex flex-col items-end gap-1">
          {LANDMARKS.map((l) => (
            <button
              key={l.key}
              data-testid={`landmark-${l.key.toLowerCase().replace(/\s+/g, "-")}-btn`}
              onClick={() => focusLandmark(l)}
              className="group flex items-center gap-2 glass-dark rounded-full px-3 py-1 text-xs hover:border-[#D4AF37] transition-[border-color,transform] hover:translate-x-[-2px]"
            >
              <span className="font-mono-tele text-[9px] text-[#F4E3A1]/70">{l.era}</span>
              <MapPin size={12} className="text-[#D4AF37]" />
              <span className="text-stone-200">{l.key}</span>
            </button>
          ))}
        </div>
      </motion.div>

      {/* ===== LEFT: Temporal Bridge — vertical (desktop) ===== */}
      <div className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 flex-col items-center gap-2 pointer-events-auto glass-light rounded-2xl px-3 py-4">
        <button data-testid="era-badge-2026" onClick={() => setTemporal(2026)} className="flex flex-col items-center hover:scale-105 transition-transform">
          <span className="font-mono-tele text-[8px] tracking-[0.2em] text-[#997316] uppercase">Present</span>
          <span className="font-serif-display text-[#1c1a17] text-sm leading-none">Memorial</span>
        </button>
        <div className="relative h-56 w-8 flex items-center justify-center my-1">
          <input
            data-testid="temporal-bridge-slider"
            type="range"
            min="2001"
            max="2026"
            step="0.1"
            value={temporal}
            onChange={(e) => {
              if (timelapse) toggleTimelapse();
              setTemporal(parseFloat(e.target.value));
            }}
            className="tab-range"
            style={{ width: "224px", transform: "rotate(-90deg)" }}
          />
        </div>
        <button data-testid="era-badge-2001" onClick={() => setTemporal(2001)} className="flex flex-col items-center hover:scale-105 transition-transform">
          <span className="font-serif-display text-[#1c1a17] text-sm leading-none">Twin Towers</span>
          <span className="font-mono-tele text-[8px] tracking-[0.2em] text-[#997316] uppercase">2001</span>
        </button>
        <div className="mt-1 flex flex-col items-center gap-1">
          <div className="font-serif-display text-[#997316] text-xl leading-none">
            {temporal >= 2024.5 ? "Present" : (
              <>
                {year}
                <span className="text-xs text-stone-400">.{Math.round((temporal - year) * 10)}</span>
              </>
            )}
          </div>
          <button
            data-testid="temporal-timelapse-btn"
            onClick={toggleTimelapse}
            className="h-8 w-8 rounded-full bg-[#D4AF37] text-[#0b0c10] grid place-items-center hover:scale-105 transition-transform"
            title="Auto time-lapse"
          >
            {timelapse ? <Pause size={14} /> : <Play size={14} />}
          </button>
        </div>
      </div>

      {/* ===== LEFT: Temporal Bridge — horizontal (mobile/tablet), bottom-left ===== */}
      <div className="md:hidden absolute left-3 bottom-3 w-[72vw] max-w-xs pointer-events-auto glass-light rounded-2xl px-3 py-2.5">
        <div className="flex items-center justify-between mb-1">
          <button data-testid="era-badge-2001-mobile" onClick={() => setTemporal(2001)} className="font-serif-display text-[#1c1a17] text-sm">2001</button>
          <span className="font-serif-display text-[#997316] text-base">{year}<span className="text-[10px] text-stone-400">.{Math.round((temporal - year) * 10)}</span></span>
          <button data-testid="era-badge-2026-mobile" onClick={() => setTemporal(2026)} className="font-serif-display text-[#1c1a17] text-sm">Present</button>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="temporal-timelapse-btn-mobile" onClick={toggleTimelapse} className="shrink-0 h-7 w-7 rounded-full bg-[#D4AF37] text-[#0b0c10] grid place-items-center">
            {timelapse ? <Pause size={13} /> : <Play size={13} />}
          </button>
          <input
            data-testid="temporal-bridge-slider-mobile"
            type="range"
            min="2001"
            max="2026"
            step="0.1"
            value={temporal}
            onChange={(e) => {
              if (timelapse) toggleTimelapse();
              setTemporal(parseFloat(e.target.value));
            }}
            className="tab-range w-full"
          />
        </div>
      </div>

      {/* Hidden era jump buttons for quick programmatic focus (desktop keeps parity) */}
      <div className="absolute left-4 top-16 hidden md:flex gap-1 pointer-events-auto">
        <button data-testid="era-badge-2026-dup" onClick={() => setTemporal(2026)} className="hidden">2026</button>
      </div>

      {/* ===== Time of day — bottom-right ===== */}
      <div className="hidden sm:block absolute bottom-4 right-4 pointer-events-auto glass-dark rounded-xl px-3 py-2.5 w-48">
        <div className="flex items-center justify-between mb-1.5">
          <span className="font-mono-tele text-[9px] tracking-widest text-stone-300">TIME OF DAY</span>
          <span className="font-mono-tele text-[10px] text-[#F4E3A1]">
            {String(hh).padStart(2, "0")}:{String(mm).padStart(2, "0")}
          </span>
        </div>
        <input
          data-testid="time-of-day-slider"
          type="range"
          min="0"
          max="24"
          step="0.1"
          value={timeOfDay}
          onChange={(e) => setTimeOfDay(parseFloat(e.target.value))}
          className="tod-range w-full"
        />
      </div>

      {/* ===== Telemetry — bottom-left with scrim ===== */}
      <div className="hidden sm:block absolute bottom-4 left-4 md:left-24 glass-dark rounded-lg px-3 py-2 font-mono-tele text-[10px] tracking-widest text-stone-200 space-y-0.5">
        <div>40.7127° N · 74.0134° W</div>
        <div data-testid="fps-telemetry-display">FPS {fps} · DPR AUTO</div>
        <div className="text-[#F4E3A1]">
          {String(hh).padStart(2, "0")}:{String(mm).padStart(2, "0")} · {effWeather.toUpperCase()} · {navMode.toUpperCase()}
        </div>
      </div>

      {/* D-Pad — piloted controls for every transport mode (centered bottom) */}
      {navMode !== "orbit" && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1">
          <DPad />
          <span data-testid="zoom-hint" className="font-mono-tele text-[8px] tracking-[0.2em] text-white/50 uppercase">
            Scroll / pinch to zoom
          </span>
        </div>
      )}
    </div>
  );
}
